import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Box, useMediaQuery } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useT } from '@transifex/react';
import { Chip, ProductLabel, Tooltip, Typography } from '@raisin/components-lib';
import { useAuthContext } from '@raisin/cp-layout';
import { getDistributor, isGBRLocale } from '@raisin/cp-utils';
import {
  BANK_EVENTS,
  BANK_PAGES,
  trackBankDetailsPageClick,
  trackProductExpanded,
} from 'eventsTracking';
import { useProductsAccess } from 'hooks';
import useOfferOrderLink from 'hooks/useOfferOrderLink';
import useResponsiveText from 'hooks/useResponsiveText';
import { DBSProduct, OFFER_PRODUCT_TYPES } from 'types';
import { formatCurrencyPosition } from 'utils/bankPageUtils';
import { formatDefaultRate, formatTerm } from 'utils/bankPageUtils/bankOffers';
import { PRODUCT_TYPES } from 'utils/BIPSMappingToOBS/deposits';
import { getMarketingProductType } from 'utils/getMarketingProductType';
import { BankOffersDetail } from '../../BankOffersDetail/v2';
import messages from '../../messages';
import * as S from './styles';

export const productTypeMessages = {
  [PRODUCT_TYPES.TERM]: messages.bankProductTypeChipTerm,
  [PRODUCT_TYPES.OVERNIGHT]: messages.bankProductTypeChipOvernight,
  [PRODUCT_TYPES.NOTICE]: messages.bankProductTypeChipNotice,
};

export interface BankOffersListItemProps {
  offer: DBSProduct;
  forceExpand?: boolean;
  testId?: string;
  isMainOfferDisplayed?: boolean;
  expandedOffers: Array<string>;
  setExpandedOffers: Function | null | undefined;
  showTooltip?: boolean;
  setIsPromotedProductOpened: Function | null | undefined;
  promotedProductId: string | null | undefined;
  depositGuaranteeColumnWidth: { [key: string]: null | number };
}

export const BankOffersListItem: React.FC<BankOffersListItemProps> = ({
  testId = 'offers-list-item',
  offer,
  forceExpand,
  expandedOffers,
  setExpandedOffers,
  showTooltip = true,
  setIsPromotedProductOpened,
  promotedProductId,
}) => {
  const t = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const tooltipRef = useRef();
  const { locale, formatNumber } = intl;
  const { isLoggedIn } = useAuthContext();
  const { distributorId } = useProductsAccess();
  const orderLink = useOfferOrderLink(locale, offer, isLoggedIn);
  const isGBR = isGBRLocale(locale);
  const marketedProductType = getMarketingProductType(offer);
  const isSustainableDeposit = offer?.display?.labels?.includes('sustainable_deposit');
  const commonEventData = {
    distributorId: distributorId || getDistributor(),
    interestRates: {
      interest_rate_nominal: offer?.interest_rates?.interest_rate_nominal,
      interest_rate_effective: offer?.interest_rates?.interest_rate_effective,
    },
    productId: offer?.id,
    product_type: marketedProductType,
    bank_id: offer?.deposit_taking_bank?.id,
  };

  const isOfferExpanded = useMemo(
    () => expandedOffers.includes(offer?.id),
    [expandedOffers, offer?.id],
  );

  const orderButtonHandler = (event) => {
    event.stopPropagation();

    trackBankDetailsPageClick({
      ...commonEventData,
      eventName: BANK_EVENTS?.ORDER_CTA_CLICKED,
      object: 'order_cta',
      bankPage: BANK_PAGES.PRODUCT_PAGE,
    });
  };

  const handleClick = () => {
    let updatedExpandedOffers = [...expandedOffers];

    if (updatedExpandedOffers.includes(offer.id)) {
      updatedExpandedOffers = updatedExpandedOffers.filter((id) => id !== offer.id);
    } else {
      if (offer.id !== promotedProductId) setIsPromotedProductOpened?.(true);
      updatedExpandedOffers.push(offer.id);
    }

    setExpandedOffers?.(updatedExpandedOffers);

    if (isOfferExpanded === false) {
      trackProductExpanded({
        ...commonEventData,
        eventName: BANK_EVENTS.PRODUCT_EXPANDED,
        bankPage: BANK_PAGES.DETAIL_PAGE,
      });
    }
  };
  const showHideTooltip = (showTooltip: boolean) => {
    if (tooltipRef?.current) {
      (tooltipRef.current as HTMLElement).style.display = showTooltip ? 'block' : 'none';
    }
  };
  const toolTip = isOfferExpanded ? 'bankOfferListItemTooltipExpanded' : 'bankOfferListItemTooltip';
  const isLongText = useResponsiveText([
    t(messages.bankOffersListItemRate),
    formatDefaultRate(offer, locale, formatNumber, t),
    t(messages.bankOffersListItemDuration),
    formatTerm(offer, intl, t),
    t(messages.bankOffersListItemProductType),
    t(productTypeMessages[marketedProductType]),
  ]);

  const showTermInProductRow = useMemo(
    () =>
      isGBR ||
      marketedProductType === OFFER_PRODUCT_TYPES.TERM ||
      marketedProductType === OFFER_PRODUCT_TYPES.NOTICE,
    [marketedProductType],
  );

  const offerDGSAmount = offer?.deposit_taking_bank.statutory_deposit_guarantee_scheme
    ?.insured_amount as string;
  const offerDGSCurrency = offer?.deposit_taking_bank.statutory_deposit_guarantee_scheme?.currency;

  const [bankOfferRateValue, bankOfferRatePeriod] = formatDefaultRate(
    offer,
    locale,
    formatNumber,
    t,
  ).split(' ');

  return (
    <S.OfferCard sx={{ my: 2 }} data-testid={testId} id={offer?.id}>
      <Tooltip
        content={showTooltip ? t(messages[toolTip]) : ''}
        disableHoverListener={!showTooltip}
        followCursor
        placement="bottom-start"
        slotProps={{
          popper: {
            ref: tooltipRef,
            modifiers: [
              {
                name: 'offset',
              },
            ],
          },
        }}
      >
        <S.HoverContainer
          data-testid="hover-container"
          onClick={handleClick}
          $disableHoverEffect={!showTooltip}
        >
          <S.GridContainer
            container
            $isSustainableDeposit={isSustainableDeposit}
            $isMobile={isMobile}
          >
            <S.RateGridItem item xs={12} sm={2} $hasMinHeight={isGBR && !showTermInProductRow}>
              <S.bankOfferRateValue>
                {bankOfferRateValue && (
                  <Typography variant="h4" color="blue600">
                    {bankOfferRateValue}
                  </Typography>
                )}
                {bankOfferRatePeriod && (
                  <Typography variant="body1" ml={1} color="blue600">
                    {bankOfferRatePeriod}
                  </Typography>
                )}
              </S.bankOfferRateValue>
            </S.RateGridItem>

            <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {showTermInProductRow && (
                <>
                  <S.FieldLabel>
                    {marketedProductType === OFFER_PRODUCT_TYPES.NOTICE
                      ? t(messages.bankOffersListItemNoticePeriod)
                      : t(messages.bankOffersListItemDuration)}
                  </S.FieldLabel>
                  <Typography variant="button">{formatTerm(offer, intl, t)}</Typography>
                </>
              )}
            </Grid>

            {isGBR ? (
              <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <S.FieldLabel>{t(messages.bankOffersListItemDepositGuarantee)}</S.FieldLabel>
                <Typography variant="button">
                  <S.DepositGuaranteeColumn $isGBR={isGBR}>
                    {formatCurrencyPosition(
                      formatNumber(+offerDGSAmount, {
                        style: 'currency',
                        currency: offerDGSCurrency,
                        maximumFractionDigits: 0,
                      }),
                      locale,
                    )}
                  </S.DepositGuaranteeColumn>
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <S.FieldLabel>{t(messages.bankOffersListItemCurrency)}</S.FieldLabel>
                <S.ProductInfo>{offer?.currency}</S.ProductInfo>
              </Grid>
            )}

            <S.ProductItem item xs={12} $isLongText={isLongText} sm={2}>
              <S.FieldLabel>{t(messages.bankOffersListItemProductType)}</S.FieldLabel>
              <Tooltip content={t(productTypeMessages[marketedProductType])} followCursor>
                <S.ChipWrapper
                  onMouseEnter={() => showHideTooltip(false)}
                  onMouseLeave={() => showHideTooltip(true)}
                >
                  <Chip
                    color="secondary"
                    label={t(productTypeMessages[marketedProductType])}
                    size="medium"
                  />
                </S.ChipWrapper>
              </Tooltip>
            </S.ProductItem>

            <S.ProductItemRow $isLongText={isLongText} xs={12} sm={2}>
              <Tooltip content={t(productTypeMessages[marketedProductType])}>
                <Chip
                  size="medium"
                  color="secondary"
                  label={t(productTypeMessages[marketedProductType])}
                />
              </Tooltip>
            </S.ProductItemRow>

            {isMobile && isSustainableDeposit && (
              <Grid item xs={12}>
                <Box p={0}>
                  <ProductLabel type="SustainableDeposit" />
                </Box>
              </Grid>
            )}

            <S.CtaContainerGrid item xs={12} sm={2}>
              <S.OrderButton
                data-testid="order-button"
                mode={forceExpand ? 'primary' : 'secondary'}
                size="medium"
                href={orderLink}
                onClick={(event) => orderButtonHandler(event)}
                onMouseOver={() => showHideTooltip(false)}
                onMouseOut={() => showHideTooltip(true)}
              >
                {t(messages.bankOffersListItemCta)}
              </S.OrderButton>
            </S.CtaContainerGrid>
          </S.GridContainer>
          {!isMobile && isSustainableDeposit && (
            <S.ProductLabelGrid container>
              <Box p={0}>
                <ProductLabel type="SustainableDeposit" />
              </Box>
            </S.ProductLabelGrid>
          )}
        </S.HoverContainer>
      </Tooltip>
      <Collapse
        in={isOfferExpanded || forceExpand}
        unmountOnExit
        onExited={() => {
          setIsPromotedProductOpened?.(false);
        }}
      >
        <BankOffersDetail offer={offer} />
      </Collapse>
    </S.OfferCard>
  );
};
